import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Wallet, Plus, Pencil, Trash2, RefreshCw, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const emptyForm = { designation: '', daily_da: '', hotel_limit: '', local_conveyance: '', travel_mode: '' };

export default function AllowanceMasterTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/journey/allowances/admin');
      setRows(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load allowances'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = r => { setEditingKey(r.designation); setEditForm({ ...r }); };
  const cancelEdit = () => { setEditingKey(null); setEditForm({}); };

  const saveEdit = async () => {
    try {
      await api.put(`/api/journey/allowances/${encodeURIComponent(editingKey)}`, {
        daily_da: editForm.daily_da,
        hotel_limit: editForm.hotel_limit,
        local_conveyance: editForm.local_conveyance,
        travel_mode: editForm.travel_mode,
      });
      toast.success(`Updated allowance for ${editingKey}`);
      cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const handleDelete = async (designation) => {
    if (!window.confirm(`Delete allowance for "${designation}"? Employees with this designation will fall back to Default.`)) return;
    try {
      await api.delete(`/api/journey/allowances/${encodeURIComponent(designation)}`);
      toast.success(`Deleted ${designation}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleAdd = async () => {
    if (!addForm.designation.trim()) { toast.error('Designation is required'); return; }
    try {
      await api.post('/api/journey/allowances', addForm);
      toast.success(`Added ${addForm.designation}`);
      setAddOpen(false);
      setAddForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  return (
    <div className="space-y-4" data-testid="allowance-master-tab">
      <div className="bg-[#0D2850]/5 border border-[#0D2850]/15 rounded-lg p-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#0D2850] flex items-center gap-2"><Wallet className="w-4 h-4" /> Allowance Master</h3>
          <p className="text-xs text-slate-600 mt-1">Set per-designation daily food (DA), hotel, local conveyance limits, and travel mode. Changes apply instantly to any expense filed next.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="allowance-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
          <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246]" onClick={() => setAddOpen(true)} data-testid="allowance-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Designation</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Designation-wise Allowances</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Designation</TableHead>
                <TableHead className="text-right">Daily Food (DA) ₹</TableHead>
                <TableHead className="text-right">Hotel Limit ₹</TableHead>
                <TableHead className="text-right">Local Conveyance ₹</TableHead>
                <TableHead>Travel Mode</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => {
                const isEditing = editingKey === r.designation;
                return (
                  <TableRow key={r.designation} className="hover:bg-slate-50" data-testid={`allowance-row-${r.designation}`}>
                    <TableCell className="font-medium text-slate-800">
                      {r.designation}
                      {r.designation === 'default' && <span className="ml-2 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Fallback</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing
                        ? <Input type="number" min="0" value={editForm.daily_da} onChange={e => setEditForm({...editForm, daily_da: e.target.value})} className="h-7 text-xs text-right" data-testid={`edit-daily-da-${r.designation}`} />
                        : `₹${r.daily_da?.toLocaleString('en-IN') || 0}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing
                        ? <Input type="number" min="0" value={editForm.hotel_limit} onChange={e => setEditForm({...editForm, hotel_limit: e.target.value})} className="h-7 text-xs text-right" data-testid={`edit-hotel-${r.designation}`} />
                        : `₹${r.hotel_limit?.toLocaleString('en-IN') || 0}`}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing
                        ? <Input type="number" min="0" value={editForm.local_conveyance} onChange={e => setEditForm({...editForm, local_conveyance: e.target.value})} className="h-7 text-xs text-right" data-testid={`edit-local-${r.designation}`} />
                        : `₹${r.local_conveyance?.toLocaleString('en-IN') || 0}`}
                    </TableCell>
                    <TableCell>
                      {isEditing
                        ? <Input value={editForm.travel_mode} onChange={e => setEditForm({...editForm, travel_mode: e.target.value})} className="h-7 text-xs" data-testid={`edit-mode-${r.designation}`} />
                        : <span className="text-xs text-slate-600">{r.travel_mode}</span>}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 bg-[#0D2850] hover:bg-[#0B2246]" onClick={saveEdit} data-testid={`save-${r.designation}`}><Save className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="h-7" onClick={cancelEdit} data-testid={`cancel-${r.designation}`}><X className="w-3 h-3" /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(r)} data-testid={`edit-${r.designation}`}><Pencil className="w-3.5 h-3.5 text-[#0D2850]" /></Button>
                          {r.designation !== 'default' && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(r.designation)} data-testid={`delete-${r.designation}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-6">No allowances configured</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <p className="text-[11px] text-slate-500 mt-3">
            <strong>How matching works:</strong> An employee's designation is matched exactly to a row here. If none matches, the <em>default</em> row is used. Any expense filed after a change picks up the new limits immediately.
          </p>
        </CardContent>
      </Card>

      {/* Add Designation Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-allowance-dialog">
          <DialogHeader><DialogTitle>Add Designation Allowance</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Designation *</Label><Input value={addForm.designation} onChange={e => setAddForm({...addForm, designation: e.target.value})} placeholder="e.g. Regional Head" data-testid="add-designation-input" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Daily DA ₹</Label><Input type="number" min="0" value={addForm.daily_da} onChange={e => setAddForm({...addForm, daily_da: e.target.value})} data-testid="add-daily-da" /></div>
              <div><Label className="text-xs">Hotel ₹</Label><Input type="number" min="0" value={addForm.hotel_limit} onChange={e => setAddForm({...addForm, hotel_limit: e.target.value})} data-testid="add-hotel" /></div>
              <div><Label className="text-xs">Local ₹</Label><Input type="number" min="0" value={addForm.local_conveyance} onChange={e => setAddForm({...addForm, local_conveyance: e.target.value})} data-testid="add-local" /></div>
            </div>
            <div><Label className="text-xs">Travel Mode</Label><Input value={addForm.travel_mode} onChange={e => setAddForm({...addForm, travel_mode: e.target.value})} placeholder="e.g. Flight / AC Train" data-testid="add-mode" /></div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleAdd} data-testid="add-save-btn">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
